// apps/client/src/main.tsx (M0-T03 shell + M0-T10 boot steps 2–4, §8.2; M0-T18 adds the game bridge)
import './ui/tokens.css';
import { createRoot } from 'react-dom/client';
import { api } from './api/endpoints';
import { installMusic } from './audio/music';
import { HttpError, onUnauthorized } from './api/http';
import { getToken } from './api/token';
import { IS_DEV, IS_E2E } from './boot/env';
import { installErrorReporting } from './boot/errors';
import { installLaunchHooks } from './boot/launchHooks';
import { registerPwa } from './boot/pwa';
import { perfFlag } from './engine/perf';
import { installGameBridge } from './game/instance';
import { useGame } from './state/store';
import { installBackdrop } from './title/backdrop';
import { App } from './ui/App';

installErrorReporting();
registerPwa();
const perf = perfFlag(window.location.search);                    // §15 device pass: ?perf shows the overlay on a phone
if (perf !== null) useGame.getState().setSettings({ perfOverlay: perf });
// Title screen (2026-10-05): every launch opens on the title, except the page load that brings you back from Sign in with Apple, which
// you left from the sign-in screen after Play now.
if (new URLSearchParams(window.location.search).get('signedIn') === '1') useGame.setState({ titleStep: 'done' });
createRoot(document.getElementById('ui')!).render(<App />);
installGameBridge();                                              // §8.2 step 5: enterTownRequest → getGame().cmd.enterTown
installMusic();                                                   // title screen (2026-10-05): the title theme, then the town music
installBackdrop();                                                // …and the live 3D town behind the screens before a town
if (IS_DEV || IS_E2E) installLaunchHooks();                       // window.__wtLaunch: the title, its music and backdrop, for tests
onUnauthorized(() => { useGame.getState().setMe(null); useGame.getState().setPhase('signedOut'); });

async function boot(): Promise<void> {
  const s = useGame.getState();
  try { s.setConfig(await api.config()); } catch { /* offline: sign-in screen shows email only */ }
  const url = new URL(window.location.href);
  if (url.searchParams.get('signedIn') === '1') {                 // returning from Sign in with Apple (D-44)
    await api.finishApple().catch(() => null);
    url.searchParams.delete('signedIn');                          // keep the path, so a pending /invite/<code> survives (rev 10 item 9)
    history.replaceState(null, '', url.pathname + url.search + url.hash);
  }
  if (!getToken()) { s.setPhase('signedOut'); return; }
  try {
    const me = await api.me();
    s.setMe(me);
    s.setPhase(me.profile ? 'towns' : 'needsProfile');
  } catch (e) {
    s.setPhase('signedOut');
    if (!(e instanceof HttpError && e.status === 401)) s.toast('errors.server_error', undefined, 'warn');
  }
}
void boot();
